import { clientLogger } from "@ka-astro/utils";
import { LocationOutlineIcon } from "@kds-web/icons";
import { useEffect, useState } from "react";
import type { JSX } from "react/jsx-runtime";
import SearchCombobox, {
  type Suggestion,
} from "../SearchCombobox/SearchCombobox";
import { transformLocationToSuggestionResults } from "../utils";

type Props = {
  onFormSubmit: () => void;
  locationSuggestionsUrl: string;
  setLocationQuery: (locationId: string) => void;
  locationQuery: string;
};

function LocationSelection({
  onFormSubmit,
  locationSuggestionsUrl,
  setLocationQuery,
  locationQuery,
}: Props): JSX.Element {
  const [locationId, setLocationId] = useState<number>();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [shouldSubmit, setShouldSubmit] = useState(false);

  useEffect(() => {
    if (shouldSubmit) {
      onFormSubmit();
      setShouldSubmit(false);
    }
  }, [shouldSubmit, onFormSubmit]);

  function handleSelect(suggestion?: Suggestion): void {
    if (suggestion) {
      setLocationQuery(suggestion.text);
      setLocationId(suggestion.selectedId);
    }
    setShouldSubmit(true);
  }

  async function updateSuggestions(query: string): Promise<void> {
    try {
      // If the query is empty, don't fetch suggestions
      if (query.length < 1) {
        setSuggestions([]);
        return;
      }
      // @TODO: temporarily use fetch instead of observedFetch
      const response = await fetch(`${locationSuggestionsUrl}?query=${query}`);
      if (response.ok === false) {
        throw new Error("Failed to fetch location suggestions");
      }
      const data = await response.json();
      const searchResults = transformLocationToSuggestionResults(data);
      setSuggestions(searchResults);
    } catch (error) {
      clientLogger.error("Failed to fetch location suggestions:", error);
    }
  }

  async function handleUserInputChange(userInput: string): Promise<void> {
    setLocationQuery(userInput);
    await updateSuggestions(userInput);
  }

  return (
    <>
      <LocationOutlineIcon size="medium" className="mx-xsmall" />
      <SearchCombobox
        placeholder="PLZ oder Ort"
        suggestions={suggestions}
        inputValue={locationQuery}
        onInputChange={handleUserInputChange}
        inputName="locationStr"
        onSelect={handleSelect}
        listId="search-location-list"
        dataTestId="search-location-combobox"
      />
      <input name="locationId" className="hidden" value={locationId} />
    </>
  );
}

export default LocationSelection;
